import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo, useRef, useCallback } from "react";
import {
  LayoutGrid,
  AlertCircle,
  RefreshCw,
  Upload,
  ShieldCheck,
  MoreHorizontal,
  Trash2,
} from "lucide-react";
import {
  ActionMenu,
  Alert,
  Badge,
  Button,
  ErrorPage,
  PageContent,
  PageHeader,
  PageShell,
  PageSkeleton,
} from "@h2j-data-team/ui";
import type { GroupApp, ConflictResolution } from "@repo/shared";
import { BackLink } from "@/components/back-link";
import { ConfirmDialog, useConfirmDialog, toast } from "@/components/shared";
import { supportCode } from "@/components/error-screen";
import { BackupRecommendationDialog } from "@/components/shared/backup-recommendation-dialog";
import {
  groupDetailQueryOptions,
  useDeleteGroup,
  useRemoveGroupApp,
  useAuditGroup,
  useSyncGroup,
} from "@/features/groups/api";
import { appMetadataQueryOptions } from "@/features/qlik-connections/api";
import { ApiError, getErrorMessage } from "@/lib/api-client";
import {
  validateGroupExpressions,
  type AppExpressionCheckResult,
  type SyncGroupOptions,
} from "@/lib/api/groups";
import { getBackupAlertDismissed } from "@/lib/use-backup-alert-preference";
import { GroupItemsEditor } from "@/features/groups/components/group-items-editor";
import { ManageAppsDrawer } from "@/features/groups/components/manage-apps-drawer";
import { GroupAddAppsDrawer } from "@/features/groups/components/group-add-apps-drawer";
import { UnifiedImportDrawer } from "@/features/groups/components/unified-import-drawer";
import { ConflictResolutionDialog } from "@/features/groups/components/conflict-resolution-dialog";

export const Route = createFileRoute("/_authenticated/sync-groups/$groupId")({
  staticData: { pageTitle: "breadcrumbs.syncGroups", breadcrumb: "breadcrumbs.groupDetail" },
  validateSearch: (search: Record<string, unknown>) => {
    const out: { tab?: "variables" | "dimensions" | "measures"; item?: string } = {};
    const tab = search.tab;
    if (tab === "variables" || tab === "dimensions" || tab === "measures") out.tab = tab;
    if (typeof search.item === "string") out.item = search.item;
    return out;
  },
  component: GroupDetailPage,
});

function compareAlphabetically(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

function GroupDetailPage() {
  const { t } = useTranslation("sync-groups");
  const { t: tc } = useTranslation("common");
  const { groupId } = Route.useParams();
  const navigate = useNavigate({ from: Route.fullPath });
  const { tab: urlTab, item: urlItem } = Route.useSearch();
  const { data: group, isLoading, error, refetch } = useQuery(groupDetailQueryOptions(groupId));
  const deleteMutation = useDeleteGroup();
  const removeAppMutation = useRemoveGroupApp();
  const auditMutation = useAuditGroup();
  const syncMutation = useSyncGroup();
  const { confirm, dialogProps } = useConfirmDialog();
  const [addAppsDialogOpen, setAddAppsDialogOpen] = useState(false);
  const [manageAppsOpen, setManageAppsOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const appsButton = useRef<HTMLButtonElement>(null);
  const moreButton = useRef<HTMLButtonElement>(null);
  const [conflictResolutions, setConflictResolutions] = useState<
    Record<string, ConflictResolution>
  >({});
  const [isValidating, setIsValidating] = useState(false);
  const [validationResults, setValidationResults] = useState<AppExpressionCheckResult[]>([]);

  const outOfSyncVariables = useMemo(
    () =>
      group?.variables.filter((variable) =>
        variable.syncStatus.some((status) => status.status === "out_of_sync"),
      ) ?? [],
    [group?.variables],
  );

  const outOfSyncDimensions = useMemo(
    () =>
      group?.dimensions.filter((dimension) =>
        dimension.syncStatus.some((status) => status.status === "out_of_sync"),
      ) ?? [],
    [group?.dimensions],
  );

  const outOfSyncMeasures = useMemo(
    () =>
      group?.measures.filter((measure) =>
        measure.syncStatus.some((status) => status.status === "out_of_sync"),
      ) ?? [],
    [group?.measures],
  );

  const missingVariables = useMemo(
    () =>
      group?.variables.filter((variable) =>
        variable.syncStatus.some((status) => status.status === "missing"),
      ) ?? [],
    [group?.variables],
  );

  const missingDimensions = useMemo(
    () =>
      group?.dimensions.filter((dimension) =>
        dimension.syncStatus.some((status) => status.status === "missing"),
      ) ?? [],
    [group?.dimensions],
  );

  const missingMeasures = useMemo(
    () =>
      group?.measures.filter((measure) =>
        measure.syncStatus.some((status) => status.status === "missing"),
      ) ?? [],
    [group?.measures],
  );

  const totalNeedsAttention =
    outOfSyncVariables.length +
    outOfSyncDimensions.length +
    outOfSyncMeasures.length +
    missingVariables.length +
    missingDimensions.length +
    missingMeasures.length;

  const hasItems =
    (group?.variables.length ?? 0) > 0 ||
    (group?.dimensions.length ?? 0) > 0 ||
    (group?.measures.length ?? 0) > 0;

  const sortedVariables = useMemo(
    () => [...(group?.variables ?? [])].sort((a, b) => compareAlphabetically(a.name, b.name)),
    [group?.variables],
  );

  const sortedDimensions = useMemo(
    () => [...(group?.dimensions ?? [])].sort((a, b) => compareAlphabetically(a.title, b.title)),
    [group?.dimensions],
  );

  const sortedMeasures = useMemo(
    () => [...(group?.measures ?? [])].sort((a, b) => compareAlphabetically(a.title, b.title)),
    [group?.measures],
  );

  const firstGroupApp = group?.apps[0];
  const { data: firstGroupAppMetadata } = useQuery({
    ...appMetadataQueryOptions(firstGroupApp?.connectionId ?? "", firstGroupApp?.appId ?? ""),
    enabled: !!firstGroupApp,
  });

  const handleAudit = async () => {
    try {
      const job = await auditMutation.mutateAsync(groupId);
      navigate({ to: "/jobs/$id", params: { id: job.id } });
    } catch (err) {
      toast.error(getErrorMessage(err, t("detail.failedAudit")));
    }
  };

  const runSyncValidation = useCallback(
    async (
      options?: SyncGroupOptions,
      { notifyOnSuccess = false, allowWorkerFallback = false } = {},
    ) => {
      setIsValidating(true);
      setValidationResults([]);
      try {
        const result = await validateGroupExpressions(groupId, options);
        if (!result.valid) {
          setValidationResults(result.results);
          return false;
        }

        if (notifyOnSuccess) {
          toast.success(t("validation.allValid"));
        }

        return true;
      } catch (err) {
        if (allowWorkerFallback) {
          toast.warning(t("validation.fallbackToJob"));
          return true;
        }

        toast.error(getErrorMessage(err, t("validation.failedToValidate")));
        return false;
      } finally {
        setIsValidating(false);
      }
    },
    [groupId, t],
  );

  const handleValidate = async () => {
    await runSyncValidation(undefined, { notifyOnSuccess: true });
  };

  const performSync = async () => {
    if (
      outOfSyncVariables.length > 0 ||
      outOfSyncDimensions.length > 0 ||
      outOfSyncMeasures.length > 0 ||
      missingVariables.length > 0 ||
      missingDimensions.length > 0 ||
      missingMeasures.length > 0
    ) {
      const defaults: Record<string, ConflictResolution> = {};
      for (const variable of outOfSyncVariables) {
        defaults[variable.id] = conflictResolutions[variable.id] ?? "push";
      }
      for (const dimension of outOfSyncDimensions) {
        defaults[dimension.id] = conflictResolutions[dimension.id] ?? "push";
      }
      for (const measure of outOfSyncMeasures) {
        defaults[measure.id] = conflictResolutions[measure.id] ?? "push";
      }
      for (const variable of missingVariables) {
        defaults[variable.id] = "push";
      }
      for (const dimension of missingDimensions) {
        defaults[dimension.id] = "push";
      }
      for (const measure of missingMeasures) {
        defaults[measure.id] = "push";
      }
      setConflictResolutions(defaults);
      setConflictDialogOpen(true);
      return;
    }

    try {
      const isValid = await runSyncValidation(undefined, { allowWorkerFallback: true });
      if (!isValid) {
        return;
      }

      const job = await syncMutation.mutateAsync({ groupId });
      navigate({ to: "/jobs/$id", params: { id: job.id } });
    } catch (err) {
      toast.error(getErrorMessage(err, t("detail.failedSync")));
    }
  };

  const handleSync = () => {
    if (getBackupAlertDismissed()) {
      void performSync();
      return;
    }
    setBackupDialogOpen(true);
  };

  const handleDeleteGroup = () => {
    confirm({
      title: t("detail.deleteTitle"),
      description: t("detail.deleteDescription", { name: group?.name ?? "" }),
      confirmLabel: t("detail.deleteConfirm"),
      variant: "destructive",
      restoreFocusRef: moreButton,
      onConfirm: async () => {
        await deleteMutation.mutateAsync(groupId);
        navigate({ to: "/sync-groups", search: { q: undefined } });
      },
    });
  };

  const handleRemoveApp = (app: GroupApp) => {
    confirm({
      title: t("detail.removeAppTitle"),
      description: t("detail.removeAppDescription", { name: app.appName }),
      confirmLabel: t("detail.removeAppConfirm"),
      variant: "destructive",
      onConfirm: async () => {
        await removeAppMutation.mutateAsync({ groupId, groupAppId: app.id });
      },
    });
  };

  if (isLoading) {
    return <PageSkeleton variant="detail" label={t("detail.loadingTitle")} />;
  }

  if (error || !group) {
    // A missing group is a 404 (or no error at all); anything else is a load failure worth retrying.
    const notFound = !error || (error instanceof ApiError && error.status === 404);
    return (
      <ErrorPage
        kind="load-failed"
        header={
          <PageHeader
            title={t("list.pageTitle")}
            back={
              <BackLink
                to="/sync-groups"
                search={{ q: undefined }}
                label={t("detail.breadcrumbGroups")}
              />
            }
          />
        }
        title={notFound ? t("detail.notFoundTitle") : t("detail.loadFailedTitle")}
        description={notFound ? t("detail.notFoundDescription") : t("detail.loadFailedDescription")}
        supportCode={supportCode(error, tc("error.supportCode"))}
        actions={
          <>
            {!notFound && (
              <Button variant="outline" onClick={() => void refetch()}>
                {tc("tryAgain")}
              </Button>
            )}
            <Button asChild>
              <Link to="/sync-groups" search={{ q: undefined }}>
                {t("detail.notFoundBackButton")}
              </Link>
            </Button>
          </>
        }
      />
    );
  }

  const noApps = group.apps.length === 0;

  return (
    <PageShell
      header={
        <PageHeader
          title={group.name}
          description={group.description ?? t("detail.defaultDescription")}
          back={
            <BackLink
              to="/sync-groups"
              search={{ q: undefined }}
              label={t("detail.breadcrumbGroups")}
            />
          }
          actions={
            <>
              <Button
                ref={appsButton}
                variant="outline"
                size="sm"
                onClick={() => setManageAppsOpen(true)}
              >
                <LayoutGrid />
                {group.apps.length > 0
                  ? t("apps.appsCount", { count: group.apps.length })
                  : t("apps.addApps")}
              </Button>
              <Button
                size="sm"
                onClick={handleSync}
                loading={syncMutation.isPending}
                disabled={isValidating || noApps || !hasItems}
                disabledReason={
                  noApps
                    ? t("detail.syncNeedsApps")
                    : !hasItems
                      ? t("detail.syncNeedsItems")
                      : undefined
                }
              >
                <Upload />
                {t("detail.sync")}
              </Button>
              {totalNeedsAttention > 0 && (
                <Badge
                  variant="warning"
                  className="gap-1"
                  title={t("detail.needsAttention", { count: totalNeedsAttention })}
                >
                  <AlertCircle aria-hidden />
                  {totalNeedsAttention}
                  <span className="sr-only">
                    {t("detail.needsAttention", { count: totalNeedsAttention })}
                  </span>
                </Badge>
              )}
              <ActionMenu
                label={t("detail.moreActions")}
                trigger={
                  <Button
                    ref={moreButton}
                    variant="outline"
                    size="icon-sm"
                    aria-label={t("detail.moreActions")}
                    title={t("detail.moreActions")}
                  >
                    <MoreHorizontal />
                  </Button>
                }
                items={[
                  {
                    id: "audit",
                    label: t("detail.audit"),
                    icon: <RefreshCw />,
                    disabled: auditMutation.isPending || noApps || !hasItems,
                    onSelect: () => void handleAudit(),
                  },
                  {
                    id: "validate",
                    label: t("validation.validateButton"),
                    icon: <ShieldCheck />,
                    disabled: isValidating || noApps || !hasItems,
                    onSelect: () => void handleValidate(),
                  },
                  { type: "separator", id: "separator" },
                  {
                    id: "delete",
                    label: t("detail.deleteGroup"),
                    icon: <Trash2 />,
                    destructive: true,
                    onSelect: handleDeleteGroup,
                  },
                ]}
              />
            </>
          }
        />
      }
    >
      <PageContent fill>
        {/* Expression validation errors — dismissible banner */}
        {validationResults.length > 0 && (
          <Alert
            tone="danger"
            title={t("validation.errorsTitle")}
            onDismiss={() => setValidationResults([])}
            dismissLabel={t("validation.dismiss")}
            className="mb-4 shrink-0"
          >
            <div className="max-h-48 space-y-2 overflow-y-auto">
              {validationResults.map((appResult) => (
                <div key={appResult.appId} className="space-y-1">
                  <p className="font-medium">
                    {t("validation.appLabel", { name: appResult.appName })}
                  </p>
                  <ul className="space-y-1 pl-4">
                    {appResult.results
                      .filter((r) => !r.valid)
                      .map((r) => (
                        <li key={r.id}>
                          <span className="font-mono font-medium">{r.id}</span>
                          {r.errorMsg && <span>: {r.errorMsg}</span>}
                          {r.badFieldNames.length > 0 && (
                            <span>
                              {" — "}
                              {t("validation.badFields", { fields: r.badFieldNames.join(", ") })}
                            </span>
                          )}
                          {r.dangerousFieldNames.length > 0 && (
                            <span>
                              {" — "}
                              {t("validation.dangerousFields", {
                                fields: r.dangerousFieldNames.join(", "),
                              })}
                            </span>
                          )}
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
            </div>
          </Alert>
        )}

        {/* Managed Items — master/detail editor (fills remaining height) */}
        <GroupItemsEditor
          groupId={groupId}
          variables={group.variables}
          dimensions={group.dimensions}
          measures={group.measures}
          apps={group.apps}
          availableFields={firstGroupAppMetadata?.fields}
          onImport={() => setImportDialogOpen(true)}
          initialTab={urlTab}
          initialItem={urlItem}
          onSelectionChange={(tab, item) =>
            navigate({
              search: (prev) => ({
                ...prev,
                tab: tab === "dimensions" ? undefined : tab,
                item: item ?? undefined,
              }),
              replace: true,
            })
          }
        />
      </PageContent>

      <ManageAppsDrawer
        open={manageAppsOpen}
        onOpenChange={setManageAppsOpen}
        apps={group.apps}
        onRemove={handleRemoveApp}
        onAddApps={() => setAddAppsDialogOpen(true)}
        restoreFocusRef={appsButton}
      />

      <ConfirmDialog
        {...dialogProps}
        isPending={deleteMutation.isPending || removeAppMutation.isPending}
      />

      <GroupAddAppsDrawer
        open={addAppsDialogOpen}
        onOpenChange={setAddAppsDialogOpen}
        groupId={groupId}
        existingAppIds={group.apps.map((app) => app.appId)}
      />

      <UnifiedImportDrawer
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        groupId={groupId}
        groupApps={group.apps}
        existingVariableNames={sortedVariables.map((v) => v.name)}
        existingDimensionTitles={sortedDimensions.map((d) => d.title)}
        existingMeasureTitles={sortedMeasures.map((m) => m.title)}
      />

      <BackupRecommendationDialog
        open={backupDialogOpen}
        onOpenChange={setBackupDialogOpen}
        onConfirm={performSync}
        isPending={syncMutation.isPending}
      />

      <ConflictResolutionDialog
        open={conflictDialogOpen}
        onOpenChange={setConflictDialogOpen}
        variables={outOfSyncVariables}
        dimensions={outOfSyncDimensions}
        measures={outOfSyncMeasures}
        missingVariables={missingVariables}
        missingDimensions={missingDimensions}
        missingMeasures={missingMeasures}
        conflictResolutions={conflictResolutions}
        onResolutionChange={setConflictResolutions}
        isSyncing={syncMutation.isPending || isValidating}
        syncPending={syncMutation.isPending}
        onConfirm={async () => {
          const isActionable = (id: string) => (conflictResolutions[id] ?? "push") !== "skip";

          const variableIds = [
            ...outOfSyncVariables.map((v) => v.id),
            ...missingVariables.map((v) => v.id),
          ].filter(isActionable);
          const dimensionIds = [
            ...outOfSyncDimensions.map((d) => d.id),
            ...missingDimensions.map((d) => d.id),
          ].filter(isActionable);
          const measureIds = [
            ...outOfSyncMeasures.map((m) => m.id),
            ...missingMeasures.map((m) => m.id),
          ].filter(isActionable);

          if (variableIds.length === 0 && dimensionIds.length === 0 && measureIds.length === 0) {
            toast.error(t("detail.nothingToSync"));
            return;
          }

          try {
            const options = { conflictResolutions, variableIds, dimensionIds, measureIds };
            const isValid = await runSyncValidation(options, { allowWorkerFallback: true });
            if (!isValid) {
              return;
            }

            const job = await syncMutation.mutateAsync({
              groupId,
              options,
            });
            setConflictDialogOpen(false);
            navigate({ to: "/jobs/$id", params: { id: job.id } });
          } catch (err) {
            toast.error(getErrorMessage(err, t("detail.failedSync")));
          }
        }}
      />
    </PageShell>
  );
}
